import { useEffect, useId, useRef, type JSX, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Right slide-in drawer primitive. Replaces the bespoke drawer
 * markup currently scattered across PlatformBugs / Tickets / etc.
 *
 * Behavior:
 *   - Fixed full-height overlay with backdrop click-to-close
 *   - Slides in from the right with `admin-slide-in-right` keyframe
 *   - Esc closes (when not in an input — though typed input still
 *     closes since most fields are inside the drawer body)
 *   - Optional footer slot for primary/secondary actions
 *   - Body scrolls; header + footer stay sticky
 *   - Dialog semantics: labelled by the title, focus moves into the panel
 *     on open, Tab is trapped inside, focus returns to the opener on close
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
}): JSX.Element | null {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // Move focus in on open, put it back where it was on close.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // Prefer the first form control in the body over the Close button.
    const body = panel?.querySelector<HTMLElement>('[data-drawer-body]');
    const target = body?.querySelector<HTMLElement>(FOCUSABLE)
      ?? panel?.querySelector<HTMLElement>(FOCUSABLE)
      ?? panel;
    target?.focus();
    return () => {
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [open]);

  const onPanelKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Tab') return;
    const nodes = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    if (nodes.length === 0) { e.preventDefault(); return; }
    const first = nodes[0]!;
    const last = nodes[nodes.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panelRef.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const widthPx = width === 'lg' ? 540 : width === 'sm' ? 360 : 440;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(11, 15, 25, 0.6)',
          backdropFilter: 'blur(4px)',
        }}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
        style={{
          outline: 'none',
          position: 'relative',
          width: '100%',
          maxWidth: widthPx,
          background: 'var(--bg-raised)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'admin-slide-in-right 0.18s ease-out',
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 16px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-raised)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div id={titleId} style={{ fontSize: '0.95rem', fontWeight: 600 }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 6,
              borderRadius: 8,
              cursor: 'pointer',
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X width={16} height={16} aria-hidden="true" />
          </button>
        </div>
        <div data-drawer-body style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {children}
        </div>
        {footer && (
          <div
            style={{
              borderTop: '1px solid var(--border)',
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              background: 'var(--bg-raised)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
